// work designer-plugin (порт дизайнерского цикла на work, 2026-08-21).
// Плагин в work-режиме говорит ТОЛЬКО с weern-work-backend: задачи по
// дизайну из dev-данных, лизинг задачи, исходник из R2 work, версии
// мастера в des_master_specs (мигр. 0056). Auth — X-Dev-Plugin-Token
// (dev_plugin_tokens, врезается в бандл при скачивании из Work-кабинета).
import { z } from 'zod';
import { WorkMasterFormatSchema } from '../vocab/work';

export const WorkDesignTaskSchema = z.object({
  /** Ключ задачи = locale + '|' + merchant. */
  locale: z.string(),
  merchant: z.string(),
  kind: z.string(),                 // membership | coupon | ...
  card_id: z.string(),
  /** Площадка карточки — контекст «откуда задача». */
  source: z.string(),
  /** Помечена «нужен дизайнер» (иначе — обычная позиция без мастера). */
  needs_design: z.boolean(),
  /** id passed-кадра в dev_source_frames, '' = байтов в work нет (source_ref-ссылка). */
  frame_id: z.string(),
  /** Последняя сохранённая версия мастера, 0 = ещё не сохраняли. */
  saved_version: z.number(),
  /** Занята живым клеймом другого человека. */
  busy: z.boolean(),
  /** Сколько кадров ячейка ещё примет = capSlots(); 0 в список не попадает. */
  /** Многомерчантный исходник: сколько мерчантов внутри карточки (0 = одиночный). */
  merchants_count: z.number().int().default(0),
  n_slots: z.number().default(0),
  /** Кадров ячейки, отклонённых QA и ждущих перерисовки (реворк-петля). */
  rework: z.number().int().default(0),
  /** Причины отклонений (уникальные, ≤5) — статус «нужны правки» в плагине
   *  показывается вместе с текстом QA, а не голым счётчиком (#611). */
  rework_reasons: z.array(z.string()).default([]),
  /** Последний мастер зафиксирован «готово к размножению». */
  master_ready: z.boolean().default(false),
  /** Агрегатор (source_type='aggregator' ИЛИ непустой merchants_json):
   *  в плагине живёт в блоке «Агрегаторы», размножается батчами по 100 и
   *  НЕ прячется после отправки — только по исчерпании слотов. */
  aggregator: z.boolean().default(false),
  /** Кадров текущей версии уже на сервере (pending/submitted/final/indexed) —
   *  по ним плагин красит отправленные батчи. У мерчантов всегда 0: их
   *  ячейка с отправленным прячется целиком. */
  sent_current: z.number().int().default(0),
  /** Агрегатор: мерчантов найдено (research_merchants, иначе merchants_json). */
  agg_merchants_found: z.number().int().default(0),
  /** Агрегатор купонов/гифтов: у скольких найденных мерчантов есть офферы. */
  agg_merchants_with_offers: z.number().int().default(0),
  /** Агрегатор: всего живых акций найдено (research_promos, не rejected). */
  agg_promos_found: z.number().int().default(0),
  /** Гейт активности (Глеб, 2026-08-26): купон/гифт-агрегатор кликабелен
   *  только когда конвейер принёс ≥2 исходника разных мерчантов, нашёл
   *  мерчантов и данные под манифест. false = карточка серая с причиной. */
  active: z.boolean().default(true),
  inactive_reason: z.string().default(''),
  /** Информация для размножения от «Контроля исходников» (мигр. 0085): как её
   *  ввели, валидным JSON-текстом; '' = не заполняли. Плагин читает её сам —
   *  сервер в неё не лезет и формы полей не навязывает. */
  repl_json: z.string().default(''),
  /** Квота агрегатора в локали (0 = единичный мерчант). Кап — 450 на
   *  (агрегатор × код локали) меж всех категорий. */
  agg_quota: z.number().int().default(0),
  /** Исходники ячейки (агрегатор): '' = основной; лучшее разрешение вперёд. */
  variants: z.array(z.object({
    id: z.string(),
    sub_merchant: z.string().default(''),
    w: z.number().int().default(0),
    h: z.number().int().default(0),
  })).default([]),
});
export type WorkDesignTask = z.infer<typeof WorkDesignTaskSchema>;

export const WorkDesignTasksResSchema = z.object({
  ok: z.boolean(),
  tasks: z.array(WorkDesignTaskSchema).default([]),
});
export type WorkDesignTasksRes = z.infer<typeof WorkDesignTasksResSchema>;

export const WorkDesignClaimReqSchema = z.object({
  locale: z.string().trim().min(1).max(60),
  merchant: z.string().trim().min(1).max(160),
});
export type WorkDesignClaimReq = z.infer<typeof WorkDesignClaimReqSchema>;

export const WorkDesignClaimResSchema = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  frame_id: z.string().optional(),
  saved_version: z.number().optional(),
});
export type WorkDesignClaimRes = z.infer<typeof WorkDesignClaimResSchema>;

/** POST /api/work/plugin/designRelease (2026-09-01) — отпустить СВОЙ клейм
 *  при смене задачи, не дожидаясь 30-минутного TTL. Тело — то же, что у
 *  designClaim/designHeartbeat (WorkDesignClaimReqSchema). Идемпотентно:
 *  `released=false` = своего клейма на ячейке не было (или уже отпущен).
 *  Ручка новая: старого сервера, отвечающего без `released`, не бывает —
 *  поле обязательно, дефолта нет (contracts.md: снимать форму с хендлера). */
export const WorkDesignReleaseResSchema = z.object({
  ok: z.boolean(),
  released: z.boolean(),
});
export type WorkDesignReleaseRes = z.infer<typeof WorkDesignReleaseResSchema>;

export const WorkMasterFontSchema = z.object({
  family: z.string().max(120),
  style: z.string().max(60),
});
export type WorkMasterFont = z.infer<typeof WorkMasterFontSchema>;

/** Поле, добавленное дизайнером руками поверх мастера. box — [x,y,w,h]
 *  в координатах мастера; value — текст, который дизайнер там оставил. */
export const WorkMasterAddedFieldSchema = z.object({
  key: z.string().trim().min(1).max(60),
  /** image_circle / image_square — картинка-плейсхолдер (Глеб, 2026-08-25):
   *  дизайнер ставит круг/квадрат по координатам под логотип мерчанта или
   *  картинку карты; значение подбирается данными манифеста, не текстом. */
  kind: z.enum(['text', 'timer', 'linked_code', 'image_circle', 'image_square']).default('text'),
  value: z.string().max(200).default(''),
  box: z.array(z.number()).length(4).optional(),
  timer_max_seconds: z.number().optional(),
  timer_spread_seconds: z.number().optional(),
});
export type WorkMasterAddedField = z.infer<typeof WorkMasterAddedFieldSchema>;

export const WorkMasterSaveReqSchema = z.object({
  locale: z.string().trim().min(1).max(60),
  merchant: z.string().trim().min(1).max(160),
  format: WorkMasterFormatSchema.default('original'),
  /** Добавленные дизайнером поля — триггерят контрольный AI-eval на сервере. */
  added_fields: z.array(WorkMasterAddedFieldSchema).max(40).default([]),
  /** JSON-строка weern.master_dump/2 — полный слепок нод. */
  master_json: z.string().min(2).max(4_000_000),
  /** SVG мастера (base64) — из него плагин восстанавливает фрейм 1:1. */
  master_svg_base64: z.string().min(1).max(12_000_000),
  fonts: z.array(WorkMasterFontSchema).max(60).default([]),
  frame_w: z.number().default(0),
  frame_h: z.number().default(0),
  plugin_version: z.string().max(20).default(''),
  /** «Нет статус-бара» (2026-08-25): дизайнер удалил/вырезал бар — клоны
   *  без бара, и переоткрытие сохранённого мастера его не инжектит. */
  no_status_bar: z.boolean().default(false),
  /** «Готово к размножению» (Глеб, 2026-08-25): мастер ЗАФИКСИРОВАН — карта
   *  полей финальна, пайплайн подбора данных под манифест продолжается.
   *  false = «сохранить, отложить»: версия пишется, но фиксацией не является. */
  ready: z.boolean().default(false),
  /** Вариант исходника, от которого нарисован мастер (мигр. 0074): версия
   *  привязывается к конкретному исходнику, манифест/батчи собираются от
   *  него. '' = основной исходник ячейки. */
  source_variant_id: z.string().trim().max(60).default(''),
});
export type WorkMasterSaveReq = z.infer<typeof WorkMasterSaveReqSchema>;

export const WorkMasterSaveResSchema = z.object({
  ok: z.boolean(),
  version: z.number().optional(),
  reason: z.string().optional(),
});
export type WorkMasterSaveRes = z.infer<typeof WorkMasterSaveResSchema>;

export const WorkMasterLatestResSchema = z.object({
  ok: z.boolean(),
  found: z.boolean(),
  version: z.number().optional(),
  format: WorkMasterFormatSchema.optional(),
  fonts: z.array(WorkMasterFontSchema).optional(),
  frame_w: z.number().optional(),
  frame_h: z.number().optional(),
  author_email: z.string().optional(),
  saved_at: z.string().optional(),
  /** Дизайнер удалил/вырезал статус-бар — при импорте мастера не инжектить. */
  no_status_bar: z.boolean().optional(),
  /** Вариант исходника, к которому привязана версия ('' = основной). */
  source_variant_id: z.string().optional(),
  /** Рядом с master.svg лежит master.json (полный дамп нод) — плагин может
   *  дозаполнить стили текста через masterJSON после SVG-импорта. */
  has_master_json: z.boolean().optional(),
});
export type WorkMasterLatestRes = z.infer<typeof WorkMasterLatestResSchema>;
